import { useEffect, useRef } from "react";
import L from "leaflet";
import CommonMapReact from "@containers/CommonMapReact/CommonMapReact";
import styled from "styled-components";
import { CellTowerAnalysisItem } from "@/apis/wifiCellularMismatchClient";
import { formatDistance } from "@/utils/distanceUtils";
import { translate } from "@/hooks/useLanguage";
import { LanguageKey } from "@/types/generated/LanguageKey";

const Style = styled.div`
  .external-marker {
    filter: hue-rotate(0deg) saturate(2) brightness(0.8);
  }
  .harvested-marker {
    filter: hue-rotate(100deg) saturate(2) brightness(0.9);
  }
`;

interface CellTowerAnalysisMapProps {
  towers: CellTowerAnalysisItem[];
  selectedTower?: CellTowerAnalysisItem | null;
  onSelectTower?: (tower: CellTowerAnalysisItem) => void;
}

const getLineColor = (distanceMeters: number | null): string => {
  if (distanceMeters === null) return "#6c757d";
  if (distanceMeters > 100000) return "#dc3545"; // red >100km
  if (distanceMeters > 50000) return "#fd7e14"; // orange >50km
  if (distanceMeters > 20000) return "#ffc107"; // yellow >20km
  return "#28a745"; // green <=20km
};

const CellTowerAnalysisMap = ({
  towers,
  selectedTower,
  onSelectTower,
}: CellTowerAnalysisMapProps) => {
  const map = useRef<L.Map | null>(null);
  const markersLayer = useRef<L.LayerGroup | null>(null);
  const linesLayer = useRef<L.LayerGroup | null>(null);

  useEffect(() => {
    const mapInstance = map.current;
    if (!mapInstance || !towers || towers.length === 0) return;

    // Clear previous layers
    if (markersLayer.current) {
      mapInstance.removeLayer(markersLayer.current);
    }
    if (linesLayer.current) {
      mapInstance.removeLayer(linesLayer.current);
    }

    markersLayer.current = L.layerGroup();
    linesLayer.current = L.layerGroup();

    const bounds = L.latLngBounds([]);

    // Only show towers that have both external and harvested locations
    const towersWithBoth = towers.filter(
      (t) =>
        t.harvestedFound &&
        t.externalLat !== null &&
        t.externalLng !== null &&
        t.harvestedLat !== null &&
        t.harvestedLng !== null
    );

    const markers = markersLayer.current;
    const lines = linesLayer.current;

    towersWithBoth.forEach((tower) => {
      const externalLatLng = L.latLng(tower.externalLat as number, tower.externalLng as number);
      const harvestedLatLng = L.latLng(tower.harvestedLat as number, tower.harvestedLng as number);

      // Red circle marker for external (wrong) location
      const externalMarker = L.circleMarker(externalLatLng, {
        radius: 8,
        fillColor: "#dc3545",
        color: "#721c24",
        weight: 2,
        opacity: 1,
        fillOpacity: 0.8,
      });
      externalMarker.bindTooltip(
        `<strong>${translate(LanguageKey.external_location_key)}</strong><br/>` +
          `MCC: ${tower.mcc}, MNC: ${tower.mnc}<br/>` +
          `LAC: ${tower.area}, Cell: ${tower.cellId}<br/>` +
          `${translate(LanguageKey.distance_key)}: ${formatDistance(tower.distanceMeters)}`,
        { direction: "top" }
      );
      if (onSelectTower) {
        externalMarker.on("click", () => onSelectTower(tower));
      }
      externalMarker.addTo(markers);
      bounds.extend(externalLatLng);

      // Green circle marker for harvested (correct) location
      const harvestedMarker = L.circleMarker(harvestedLatLng, {
        radius: 8,
        fillColor: "#28a745",
        color: "#155724",
        weight: 2,
        opacity: 1,
        fillOpacity: 0.8,
      });
      harvestedMarker.bindTooltip(
        `<strong>${translate(LanguageKey.harvested_location_key)}</strong><br/>` +
          `MCC: ${tower.mcc}, MNC: ${tower.mnc}<br/>` +
          `LAC: ${tower.area}, Cell: ${tower.cellId}<br/>` +
          `${translate(LanguageKey.observation_count_key)}: ${tower.observationCount?.toLocaleString() ?? "N/A"}<br/>` +
          `${translate(LanguageKey.confidence_key)}: ${tower.confidence ?? "N/A"}`,
        { direction: "top" }
      );
      if (onSelectTower) {
        harvestedMarker.on("click", () => onSelectTower(tower));
      }
      harvestedMarker.addTo(markers);
      bounds.extend(harvestedLatLng);

      // Line connecting external to harvested
      const line = L.polyline([externalLatLng, harvestedLatLng], {
        color: getLineColor(tower.distanceMeters),
        weight: 2,
        opacity: 0.7,
        dashArray: "5, 5",
      });
      line.addTo(lines);
    });

    // Add layers to map
    linesLayer.current.addTo(mapInstance);
    markersLayer.current.addTo(mapInstance);

    // Fit bounds
    if (bounds.isValid()) {
      mapInstance.fitBounds(bounds, { animate: true, maxZoom: 12, padding: [20, 20] });
    }

    return () => {
      if (markersLayer.current) mapInstance.removeLayer(markersLayer.current);
      if (linesLayer.current) mapInstance.removeLayer(linesLayer.current);
    };
  }, [towers, onSelectTower]);

  // Effect to zoom to selected tower - fit both external and harvested locations
  useEffect(() => {
    if (!map.current || !selectedTower) return;
    if (
      selectedTower.harvestedFound &&
      selectedTower.externalLat !== null &&
      selectedTower.externalLng !== null &&
      selectedTower.harvestedLat !== null &&
      selectedTower.harvestedLng !== null
    ) {
      const bounds = L.latLngBounds([
        L.latLng(selectedTower.externalLat, selectedTower.externalLng),
        L.latLng(selectedTower.harvestedLat, selectedTower.harvestedLng),
      ]);
      map.current.fitBounds(bounds, { animate: true, padding: [50, 50], maxZoom: 14 });
    }
  }, [selectedTower]);

  return (
    <Style style={{ flex: 1, minHeight: 0, position: "relative" }}>
      <CommonMapReact
        mapRef={map}
        height="calc(70vh - 200px)"
        options={{
          zoomPosition: "topright",
          hideCurrentLocation: true,
          hideFullScreen: true,
          showGeozoneLayer: false,
          showNetworkLayer: false,
          searchBox: true,
        }}
      />
    </Style>
  );
};

export default CellTowerAnalysisMap;
